import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ImportDialog, type ImportStep } from '@src/components/ImportDialog';
import { useGetCareOrdersQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import {
  isGridApiReady,
  logger,
  NotificationVariants,
  useCallbackRef,
  useConstant,
  useDisclosureWithContext,
  useGlobalToasts,
  useSocketClient,
  type CareOrder,
  type DialogProps,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { IsRowSelectable, SelectionChangedEvent } from 'ag-grid-community';
import { isError, isString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { BehaviorSubject, filter } from 'rxjs';
import { useFeatureFlag } from '../../../../hooks';
import { CustomUnqorkUpload } from './CustomUnqorkUpload';
import { ImportActionDialog } from './ImportActionDialog/ImportActionDialog';
import { useCareOrderImportParser, type CareOrderParserInput } from './parsers/useCareOrderImportParser';
import { useCareOrderImportParserFlag } from './parsers/useCareOrderImportParserFlag';
import { ACTION_METADATA, type ImportedCareOrder } from './types';
import { useCareOrderImportColumnsAladdin, useCareOrderImportColumnsUnqork } from './useCareOrderImportColumns';
import { importedCareOrderToRequest } from './utils';

const IMPORT_TIMEOUT_MS = 5_000;

export function CareOrderImportDialog(props: DialogProps) {
  const [state, dispatch] = useReducer(careOrderImportSlice.reducer, careOrderImportSlice.getInitialState());
  const { add: addToast } = useGlobalToasts();
  const { data: allCareOrdersMap } = useGetCareOrdersQuery({ tag: 'CareOrderImportDialog' });
  const { close: closeDialog } = props;
  const importActionDisclosure = useDisclosureWithContext<ImportedCareOrder>();
  const parser = useCareOrderImportParserFlag();
  const { useFundDefinitionAndTradableIndexesStreams } = useFeatureFlag();

  const { step, isImporting, selectedRows } = state;

  const isRowSelectable: IsRowSelectable<ImportedCareOrder> = useCallback(
    row => {
      if (row.data == null) {
        return false;
      }
      return !isImporting && ACTION_METADATA[row.data.action.type].importable;
    },
    [isImporting]
  );

  // Clear out state when dialog is opened
  useEffect(() => {
    if (props.isOpen) {
      dispatch(careOrderImportSlice.actions.resetState());
    }
  }, [props.isOpen]);

  const dataSubject = useConstant(
    new BehaviorSubject<MinimalSubscriptionResponse<ImportedCareOrder> | undefined>(undefined)
  );
  const dataObservable = useMemo(() => dataSubject.asObservable().pipe(filter(x => x != null)), [dataSubject]);
  const parseImport = useCareOrderImportParser();

  const handleImport = useCallback(
    async (input: CareOrderParserInput | undefined) => {
      if (input == null) {
        return;
      }
      try {
        const rows = await parseImport(input);

        dataSubject.next({
          initial: true,
          data: rows,
        });
        dispatch(careOrderImportSlice.actions.uploadFulfilled(rows));
      } catch (e) {
        addToast({
          text: isError(e) ? e.message : isString(e) ? e : 'Import error',
          variant: NotificationVariants.Negative,
        });
      }
    },
    [dataSubject, parseImport, addToast]
  );

  /** Invoked from the ImportActionDialog */
  const handleRowActionChange = useCallback(
    (updatedRow: ImportedCareOrder) => {
      dataSubject.next({ initial: false, data: [updatedRow] });
    },
    [dataSubject]
  );

  const client = useSocketClient();

  // Tracks state for a timeout callback, triggered when the UI doesn't complete the
  // import flow within a certain time frame after having clicked the "import X rows" button.
  const importTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // This effects get run each time we receive new care orders,
  // and looks for the ones we're expecting to receive when clicking the "import X rows" button.
  useEffect(() => {
    if (!isImporting || allCareOrdersMap == null) {
      return;
    }
    const wantedClOrdIDs = new Set(selectedRows.map(row => row.ClOrdID));
    if (wantedClOrdIDs.size === 0) {
      throw new Error('No orders to import');
    }
    const careOrdersByClOrdID = new Map<string, CareOrder>();
    for (const [, order] of allCareOrdersMap) {
      if (wantedClOrdIDs.has(order.ClOrdID)) {
        careOrdersByClOrdID.set(order.ClOrdID, order);
      }
    }

    if (careOrdersByClOrdID.size !== selectedRows.length) {
      // Still waiting to recieve _all_ new care orders...
      return;
    }

    for (const selectedRow of selectedRows) {
      const careOrder = careOrdersByClOrdID.get(selectedRow.ClOrdID);
      if (careOrder == null) {
        throw new Error(`Missing care order for ClOrdID ${selectedRow.ClOrdID}`);
      }
      // Clear timeout for showing "Timed out" message
      clearTimeout(importTimeoutRef.current.get(careOrder.ClOrdID));
    }

    dispatch(careOrderImportSlice.actions.importFulfilled());

    // Show toast
    addToast({
      text: 'Care orders successfully imported.',
      variant: NotificationVariants.Positive,
    });

    closeDialog();
  }, [allCareOrdersMap, isImporting, selectedRows, dataSubject, closeDialog, addToast]);

  const handleConfirm = useCallback(async () => {
    dispatch(careOrderImportSlice.actions.importPending());
    for (const row of selectedRows) {
      const request = importedCareOrderToRequest(row, useFundDefinitionAndTradableIndexesStreams);
      if (!request) {
        addToast({
          text: 'Failed to build a request for an order. Please try again, and reach out to support if the issue persists.',
          variant: 'NEGATIVE',
        });
        logger.error(new Error('CareOrderImportDialog: failed to build request for an order.'), { extra: row });
        return;
      }

      const cleanup = client.registerPublication(request, error => {
        addToast({
          text: `Error: ${error.error?.msg}`,
          variant: NotificationVariants.Negative,
          timeout: 5000,
          dismissable: true,
        });
      });
      setTimeout(() => cleanup(), IMPORT_TIMEOUT_MS); // wait for pub errors for a few seconds, then stop caring

      importTimeoutRef.current.set(
        row.ClOrdID,
        setTimeout(() => {
          dispatch(careOrderImportSlice.actions.importRejected());
          addToast({
            text: `Import timed out. Some orders may not have been imported.`,
            variant: NotificationVariants.Negative,
          });
          logger.error(new Error('Care order import timed out'), { extra: { request } });
        }, IMPORT_TIMEOUT_MS)
      );
    }
  }, [selectedRows, client, addToast, useFundDefinitionAndTradableIndexesStreams]);

  const handleRowSelectionChanged = useCallback(({ api, source }: SelectionChangedEvent<ImportedCareOrder>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    // without this check (Which helps ignore the state change on isImporting changing),
    // the row selection changes and the useEffect throws 'No orders to import'
    // Per @fhqvst, this (or the useEffect related to it) will be reworked
    if (source === 'selectableChanged') {
      return;
    }
    const rows = api.getSelectedRows();
    dispatch(careOrderImportSlice.actions.setSelectedRows(rows));
  }, []);

  const confirmLabel = useMemo(() => selectConfirmLabel(state), [state]);
  const confirmDisabled = useMemo(() => selectConfirmDisabled(state), [state]);

  const handleActionClick = useCallbackRef((row: ImportedCareOrder) => {
    importActionDisclosure.open(row);
  });

  const aladdinColumns = useCareOrderImportColumnsAladdin({ onClickAction: handleActionClick });
  const unqorkColumns = useCareOrderImportColumnsUnqork({ onClickAction: handleActionClick });

  const parserSpecificImportParams = useMemo(() => {
    if (parser === 'unqork') {
      return {
        columns: unqorkColumns,
        customUploadComponent: <CustomUnqorkUpload onUnqorkImport={handleImport} />,
      };
    }

    // default to aladdin
    return {
      columns: aladdinColumns,
    };
  }, [parser, handleImport, unqorkColumns, aladdinColumns]);

  return (
    <>
      <ImportDialog
        {...props}
        minWidth="960px"
        width="90vw"
        maxWidth="1200px"
        title="Import Care Orders"
        step={step}
        closeOnClickOutside={false}
        dataObservable={dataObservable}
        columns={parserSpecificImportParams.columns}
        customUploadComponent={parserSpecificImportParams.customUploadComponent}
        onConfirm={handleConfirm}
        cancelLabel="Cancel"
        confirmDisabled={confirmDisabled}
        confirmLoading={state.isImporting}
        confirmLabel={confirmLabel}
        rowID="ClOrdID"
        onUpload={handleImport}
        onSelectionChanged={handleRowSelectionChanged}
        isRowSelectable={isRowSelectable}
      />
      {/* Context is populated on open, and cleared after closing animation is completed */}
      {importActionDisclosure.context != null && (
        <ImportActionDialog
          {...importActionDisclosure}
          /* Set context explicitly to pick up the type narrowing from the nullcheck just above */
          parser={parser}
          context={importActionDisclosure.context}
          onRowActionChange={handleRowActionChange}
          key={importActionDisclosure.openId} /* Re-mount on every fresh open */
        />
      )}
    </>
  );
}

interface CareOrderImportState {
  isImporting: boolean;
  selectedRows: ImportedCareOrder[];
  step: ImportStep;
  confirmLabel: string;
}

const careOrderImportSlice = createSlice({
  name: 'careOrderImport',
  initialState: {
    isImporting: false,
    selectedRows: [],
    step: 'initial',
    confirmLabel: 'Import 0 rows',
  } as CareOrderImportState,
  reducers: {
    resetState(state) {
      const initialState = careOrderImportSlice.getInitialState();
      state.isImporting = initialState.isImporting;
      state.selectedRows = initialState.selectedRows;
      state.step = initialState.step;
      state.confirmLabel = initialState.confirmLabel;
    },
    uploadFulfilled(state, action: PayloadAction<ImportedCareOrder[]>) {
      state.step = 'preview';
    },
    importPending(state) {
      state.isImporting = true;
    },
    importFulfilled(state) {
      state.isImporting = false;
      state.selectedRows = [];
    },
    importRejected(state) {
      state.isImporting = false;
    },
    setSelectedRows(state, action) {
      state.selectedRows = action.payload;
    },
  },
});

const selectConfirmLabel = (state: CareOrderImportState) => `Import ${state.selectedRows.length} row(s)`;

const selectConfirmDisabled = (state: CareOrderImportState) =>
  state.step === 'done' || state.isImporting || state.selectedRows.length === 0;
